import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowLeftIcon,
  CircleCheckIcon,
  DocumentIcon,
  IdFrontIcon,
  UsersCheckIcon,
  WorkflowsIcon,
} from '@persona-id/design-tokens/icons';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplatePreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplatePreview';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalHeader } from 'dashboard/layouts/ModalLayout';
import {
  Blueprint,
  BlueprintBlock,
  BlueprintBlockType,
  BlueprintPreviewMap,
  BlueprintServerMetadata,
} from 'dashboard/models/blueprint';
import { Environment } from 'dashboard/models/environment';
import { BuilderPreviewMap } from 'dashboard/models/inquiry-template';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import DashboardInquiries from 'images/dashboard/onboarding/dashboard-inquiries.png';
import DashboardReports from 'images/dashboard/onboarding/dashboard-reports.png';
import DashboardVerifications from 'images/dashboard/onboarding/dashboard-verifications.png';
import DashboardWorkflows from 'images/dashboard/onboarding/dashboard-workflows.png';
import { createMockCombinedStep } from 'inquiry/lib/stubs/inquiry';
import { StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';
import { getRootDomain } from 'lib/utils/url';

export interface BlueprintDemoModalContentsProps {
  blueprintKey: string;
  blueprintsCatalogServer: BlueprintServerMetadata[];
  blueprint?: Blueprint;
  blueprintPreviews: BlueprintPreviewMap;
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  currentEnvironment?: Environment;
  subdomain?: string;
  isBlueprintAddedToSandbox: boolean;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  toPreviousScreen?: () => void;
  closeModal: () => void;
}

enum BlueprintStep {
  InquiryInput = 'inquiry_input',
  Verifications = 'verifications',
  InquiryCompleted = 'inquiry_completed',
  Workflow = 'workflow',
  Reports = 'reports',
}

const BlueprintDemoModalContents = ({
  blueprintKey,
  blueprintsCatalogServer,
  blueprint,
  blueprintPreviews,
  inquiryTemplateBuilderPreviews,
  currentEnvironment,
  subdomain,
  isBlueprintAddedToSandbox,
  handleLoadBlueprintPreview,
  handleLoadInquiryTemplateBuilderPreview,
  toPreviousScreen,
  closeModal,
}: BlueprintDemoModalContentsProps) => {
  const [selectedBlueprintStepType, setSelectedBlueprintStepType] = useState(
    BlueprintStep.InquiryInput,
  );
  const [inquiryTemplateBuilderPreview, setInquiryTemplateBuilderPreview] = useState(null);
  const [isBlueprintPreviewLoading, setIsBlueprintPreviewLoading] = useState(true);

  const previewBlueprintServerMetadata = blueprintsCatalogServer.find(
    (serverMetadata) => serverMetadata.key === blueprintKey,
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { description: blueprintDescription, 'display-name': blueprintDisplayName } =
    previewBlueprintServerMetadata;

  useEffect(() => {
    if (blueprintPreviews[blueprintKey]) {
      const blueprintPreviewBlocks = blueprintPreviews[blueprintKey].blocks;
      // TODO: Handle when Blueprint has more than one inquiry template, once such a sandbox Blueprint exists
      // Also handle when Blueprint has zero inquiry templates (API only), once such a sandbox Blueprint exists
      const inquiryTemplate = blueprintPreviewBlocks.filter(
        (block) => block['block-type'] === BlueprintBlockType.InquiryTemplate,
      )[0];
      const inquiryTemplateBuilderName = inquiryTemplate['builder-name'];
      setIsBlueprintPreviewLoading(false);

      if (inquiryTemplateBuilderPreviews[inquiryTemplateBuilderName] == null) {
        handleLoadInquiryTemplateBuilderPreview(inquiryTemplateBuilderName);
      } else {
        setInquiryTemplateBuilderPreview(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inquiryTemplateBuilderPreviews[inquiryTemplateBuilderName],
        );
      }
    } else {
      handleLoadBlueprintPreview(blueprintKey);
    }
  }, [blueprintPreviews, inquiryTemplateBuilderPreviews]);

  const blueprintPreviewInquiryTemplates = blueprintPreviews[blueprintKey]
    ? blueprintPreviews[blueprintKey].blocks.filter(
        (block) => block['block-type'] === BlueprintBlockType.InquiryTemplate,
      )
    : [];
  const blueprintPreviewReportTemplates = blueprintPreviews[blueprintKey]
    ? blueprintPreviews[blueprintKey].blocks.filter(
        (block) => block['block-type'] === BlueprintBlockType.ReportTemplate,
      )
    : [];
  const blueprintPreviewWorkflows = blueprintPreviews[blueprintKey]
    ? blueprintPreviews[blueprintKey].blocks.filter(
        (block) => block['block-type'] === BlueprintBlockType.Workflow,
      )
    : [];
  const blueprintInquiryTemplates = blueprint
    ? blueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.InquiryTemplate,
      )
    : [];
  const blueprintWorkflows = blueprint
    ? blueprint.attributes.blocks.filter((block) => block.type === BlueprintBlockType.Workflow)
    : [];

  const inquiryTemplateClientData = useMemo<StepPreview[]>(
    () =>
      (inquiryTemplateBuilderPreview ?? []).map((stepPreview: StepPreview | CombinedStepType) => {
        if (
          stepPreview === UiStepType.Document ||
          stepPreview === UiStepType.GovernmentId ||
          stepPreview === UiStepType.Selfie ||
          stepPreview === UiStepType.SelfieReflection
        ) {
          return createMockCombinedStep(stepPreview);
        } else {
          return stepPreview;
        }
      }),
    [inquiryTemplateBuilderPreview],
  );

  // Note: This component doesn't have a ModalContainer wrapper so that it can be used within an existing modal
  return (
    <>
      <ModalHeader
        prefixIcon={
          toPreviousScreen ? <ArrowLeftIcon size={22} color={Color.CoreSlate800} /> : null
        }
        onPrefixIconClick={toPreviousScreen}
        onClose={closeModal}
      >
        {blueprintDisplayName}
      </ModalHeader>
      <ModalBody>
        <Paragraph size='md' color='primary'>
          {blueprintDescription}
        </Paragraph>
        <Spacer height={2} />
        {isBlueprintPreviewLoading ? (
          <LoadingIndicator />
        ) : (
          <ModalBodyColumnWrapper>
            <div>
              <Heading size='sm'>How this solution works:</Heading>
              <Spacer height={2} />
              {/* TODO: Add BlueprintStep for API trigger, once such a sandbox Blueprint exists */}
              {blueprintPreviewInquiryTemplates.length > 0 && (
                <>
                  <BlueprintStepWrapper
                    onClick={() => setSelectedBlueprintStepType(BlueprintStep.InquiryInput)}
                    isSelected={selectedBlueprintStepType === BlueprintStep.InquiryInput}
                  >
                    <BlueprintStepLogo>
                      <IdFrontIcon color={Color.CoreSlate800} />
                    </BlueprintStepLogo>
                    <BlueprintStepContents>
                      <Label size='md' weight='bold'>
                        Customer enters data
                      </Label>
                    </BlueprintStepContents>
                  </BlueprintStepWrapper>
                  <BlueprintStepConnector />
                  <BlueprintStepWrapper
                    onClick={() => setSelectedBlueprintStepType(BlueprintStep.Verifications)}
                    isSelected={selectedBlueprintStepType === BlueprintStep.Verifications}
                  >
                    <BlueprintStepLogo>
                      <CircleCheckIcon color={Color.CoreSlate800} />
                    </BlueprintStepLogo>
                    <BlueprintStepContents>
                      <Label size='md' weight='bold'>
                        Verifications are run
                      </Label>
                    </BlueprintStepContents>
                  </BlueprintStepWrapper>
                  <BlueprintStepConnector />
                  <BlueprintStepWrapper
                    onClick={() => setSelectedBlueprintStepType(BlueprintStep.InquiryCompleted)}
                    isSelected={selectedBlueprintStepType === BlueprintStep.InquiryCompleted}
                  >
                    <BlueprintStepLogo>
                      <UsersCheckIcon color={Color.CoreSlate800} />
                    </BlueprintStepLogo>
                    <BlueprintStepContents>
                      <Label size='md' weight='bold'>
                        Inquiry is completed
                      </Label>
                    </BlueprintStepContents>
                  </BlueprintStepWrapper>
                </>
              )}
              {blueprintPreviewWorkflows.length > 0 && (
                <>
                  <BlueprintStepConnector />
                  <BlueprintStepWrapper
                    onClick={() => setSelectedBlueprintStepType(BlueprintStep.Workflow)}
                    isSelected={selectedBlueprintStepType === BlueprintStep.Workflow}
                  >
                    <BlueprintStepLogo>
                      <WorkflowsIcon color={Color.CoreSlate800} />
                    </BlueprintStepLogo>
                    <BlueprintStepContents>
                      <Label size='md' weight='bold'>
                        Workflow is triggered
                      </Label>
                    </BlueprintStepContents>
                  </BlueprintStepWrapper>
                </>
              )}
              {blueprintPreviewReportTemplates.length > 0 && (
                <>
                  <BlueprintStepConnector />
                  <BlueprintStepWrapper
                    onClick={() => setSelectedBlueprintStepType(BlueprintStep.Reports)}
                    isSelected={selectedBlueprintStepType === BlueprintStep.Reports}
                  >
                    <BlueprintStepLogo>
                      <DocumentIcon color={Color.CoreSlate800} />
                    </BlueprintStepLogo>
                    <BlueprintStepContents>
                      <Label size='md' weight='bold'>
                        Reports are run
                      </Label>
                    </BlueprintStepContents>
                  </BlueprintStepWrapper>
                </>
              )}
            </div>
            <div>
              <BlueprintStepPreview>
                {selectedBlueprintStepType === BlueprintStep.InquiryInput && (
                  <>
                    <Paragraph color='primary'>
                      Your customer starts by entering their data into this flow, which creates an
                      Inquiry. This flow is defined by the Inquiry Template named{' '}
                      <b>{blueprintPreviewInquiryTemplates[0].name}</b> and the theme can be
                      customized to match your company’s branding.
                    </Paragraph>
                    <Spacer height={2} />
                    <InquiryTemplatePreview
                      clientData={inquiryTemplateClientData}
                      scaleRatio={0.65}
                    />
                    <Spacer height={2} />
                    <BlueprintStepPreviewFooter>
                      {isBlueprintAddedToSandbox && blueprintInquiryTemplates.length > 0 && (
                        <>
                          <Link
                            to={getDashboardTemplateLink(blueprintInquiryTemplates[0])}
                            target='_blank'
                          >
                            <Button
                              id='blueprint-demo-modal__inquiry-template'
                              data-test='blueprint-demo-modal__inquiry-template'
                              variant='basic'
                            >
                              View Inquiry Template
                            </Button>
                          </Link>
                          <a
                            href={getHostedFlowLink(
                              blueprintInquiryTemplates[0],
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              subdomain,
                              currentEnvironment,
                            )}
                            target='_blank'
                            rel='noreferrer'
                          >
                            <Button
                              id='blueprint-demo-modal__hosted-flow'
                              data-test='blueprint-demo-modal__hosted-flow'
                              variant='primary'
                            >
                              Try this flow
                            </Button>
                          </a>
                        </>
                      )}
                    </BlueprintStepPreviewFooter>
                  </>
                )}
                {selectedBlueprintStepType === BlueprintStep.Verifications && (
                  <>
                    <Paragraph color='primary'>
                      Persona runs the Verifications defined in your Inquiry Template. This can
                      range from verifying physical and digital documents to biometric and database
                      verifications.
                    </Paragraph>
                    <Spacer height={2} />
                    <BlueprintStepImage src={DashboardVerifications} />
                    <Spacer height={2} />
                    {isBlueprintAddedToSandbox && (
                      <BlueprintStepPreviewFooter>
                        <Link to='/dashboard/verifications' target='_blank'>
                          <Button
                            id='blueprint-demo-modal__verifications'
                            data-test='blueprint-demo-modal__verifications'
                            variant='primary'
                          >
                            View Verifications
                          </Button>
                        </Link>
                      </BlueprintStepPreviewFooter>
                    )}
                  </>
                )}
                {selectedBlueprintStepType === BlueprintStep.InquiryCompleted && (
                  <>
                    <Paragraph color='primary'>
                      Once a user has completed the Inquiry, you can review the results in your
                      Dashboard or fetch the results via API.
                    </Paragraph>
                    <Spacer height={2} />
                    <BlueprintStepImage src={DashboardInquiries} />
                    <Spacer height={2} />
                    {isBlueprintAddedToSandbox && (
                      <BlueprintStepPreviewFooter>
                        <Link to='/dashboard/inquiries' target='_blank'>
                          <Button
                            id='blueprint-demo-modal__inquiries'
                            data-test='blueprint-demo-modal__inquiries'
                            variant='primary'
                          >
                            View Inquiries
                          </Button>
                        </Link>
                      </BlueprintStepPreviewFooter>
                    )}
                  </>
                )}
                {selectedBlueprintStepType === BlueprintStep.Workflow && (
                  <>
                    <Paragraph color='primary'>
                      The Workflow defines custom rules and processes to orchestrate your identity
                      lifecycle. For example, it’s often used to run reports after an inquiry is
                      complete. The Workflow in this solution is named{' '}
                      <b>{blueprintPreviewWorkflows[0].name}</b>.
                    </Paragraph>
                    <Spacer height={2} />
                    <BlueprintStepImage src={DashboardWorkflows} />
                    <Spacer height={2} />
                    {isBlueprintAddedToSandbox && blueprintWorkflows.length > 0 && (
                      <BlueprintStepPreviewFooter>
                        <Link to={getDashboardTemplateLink(blueprintWorkflows[0])} target='_blank'>
                          <Button
                            id='blueprint-demo-modal__workflow'
                            data-test='blueprint-demo-modal__workflow'
                            variant='primary'
                          >
                            View Workflow
                          </Button>
                        </Link>
                      </BlueprintStepPreviewFooter>
                    )}
                  </>
                )}
                {selectedBlueprintStepType === BlueprintStep.Reports && (
                  <>
                    <Paragraph color='primary'>
                      Reports are used to enrich user data from additional sources like global
                      watchlists and email/phone risk reports. The Report Templates included in this
                      solution are:
                      <UnorderedList>
                        {blueprintPreviewReportTemplates.map((rptp) => (
                          <ListItem key={`blueprint-preview-report-template-${rptp.name}`}>
                            {rptp.name}
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Paragraph>
                    <Spacer height={2} />
                    <BlueprintStepImage src={DashboardReports} />
                    <Spacer height={2} />
                    {isBlueprintAddedToSandbox && (
                      <BlueprintStepPreviewFooter>
                        <Link to='/dashboard/report-templates' target='_blank'>
                          <Button
                            id='blueprint-demo-modal__report-templates'
                            data-test='blueprint-demo-modal__report-templates'
                            variant='primary'
                          >
                            View Report Templates
                          </Button>
                        </Link>
                        <Link to='/dashboard/reports' target='_blank'>
                          <Button
                            id='blueprint-demo-modal__reports'
                            data-test='blueprint-demo-modal__reports'
                            variant='basic'
                          >
                            View Reports
                          </Button>
                        </Link>
                      </BlueprintStepPreviewFooter>
                    )}
                  </>
                )}
              </BlueprintStepPreview>
            </div>
          </ModalBodyColumnWrapper>
        )}
      </ModalBody>
    </>
  );
};

// Generate the inquiry hosted flow link (logic is taken from front-end/dashboard/components/HostedFlowCodeSnippet)
// We always default to the first Inquiry Template on the Blueprint for now
const getHostedFlowLink = (
  block: BlueprintBlock,
  subdomain: string,
  currentEnvironment: Environment,
) => {
  const inquiryTemplateId = block.token;
  return `${location.protocol}//${[subdomain, getRootDomain(location.host)]
    .filter(Boolean)
    .join('.')}/verify?inquiry-template-id=${inquiryTemplateId}&environment-id=${
    currentEnvironment.id
  }`;
};

// Generate the link to open a given template in the Dashboard
const getDashboardTemplateLink = (block: BlueprintBlock) => {
  const blockTypeToLinkMap = {
    [BlueprintBlockType.InquiryTemplate]: `/dashboard/inquiry-templates/${block.token}/${block['latest-published-version-token']}/flow`,
    [BlueprintBlockType.VerificationTemplate]: '/dashboard/verifications',
    [BlueprintBlockType.ReportTemplate]: `/dashboard/report-templates/${block.token}`,
    [BlueprintBlockType.Workflow]: `/dashboard/workflows/${block.token}/${block['latest-published-version-token']}`,
    [BlueprintBlockType.CaseTemplate]: `/dashboard/case-templates/${block.token}`,
    [BlueprintBlockType.TxnType]: `/dashboard/transaction-types/${block.token}`,
  };

  return blockTypeToLinkMap[block.type];
};

const ModalBodyColumnWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[7]};
`;

const BlueprintStepWrapper = styled.button<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[4]};
  width: 256px;
  padding: ${Spacing[4]};
  border: 2px solid ${Color.CorePurple100};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  transition: ${TransitionDuration.Fast};
  background-color: ${({ theme }) => theme.color.primary.white};
  text-align: left;

  ${({ isSelected }) =>
    isSelected &&
    css`
      border: 2px solid ${Color.CorePurple600};
    `}

  :hover {
    transform: scale(1.02);
  }
`;

const BlueprintStepLogo = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  background-color: ${Color.CorePurple100};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const BlueprintStepContents = styled('div')``;

const BlueprintStepConnector = styled('div')`
  width: 1px;
  height: ${Spacing[4]};
  border: 1px solid ${Color.CorePurple100};
  margin: auto;
`;

const BlueprintStepPreview = styled('div')`
  margin-top: ${Spacing[7]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  width: 630px;
  height: 500px;
`;

const BlueprintStepImage = styled('img')`
  width: 100%;
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const BlueprintStepPreviewFooter = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
  padding-bottom: ${Spacing[6]};

  > * {
    flex: 1;
  }
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

const ListItem = styled('li')`
  margin: ${Spacing[2]} 0px;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

export default BlueprintDemoModalContents;
